import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, ClipboardList } from 'lucide-react';

import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import ApprenantPageShell from './ApprenantPageShell';
import AdminToast from '../admin/AdminToast';
import AcademiaSelect from '../common/AcademiaSelect';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  AcademicPanelCard,
  AcademicPanelListItem,
  AcademicPanelTableWrap,
} from '../admin/academia/AcademicPanelCard';
import {
  fetchApprenantExcellence,
  respondExcellenceOrientation,
} from '../../services/academiaAdminService';
import { buildDefaultPeriodeOptions, formatPeriodeLabel } from '../../utils/excellencePeriodes';

type Dashboard = {
  periode?: string;
  periodes?: string[];
  estShortlist?: boolean;
  score?: {
    rang: number | null;
    scoreGlobal: number;
    scoreAcademique: number;
    scorePresence: number;
    scoreDiscipline: number;
    scorePratique: number;
    scoreImplicationPedagogique: number;
    scoreProjets: number;
  } | null;
  criteres?: { id: string; label: string; poids: number }[];
  orientations?: {
    id: string;
    statut: string;
    commentaireAdmin?: string;
    opportunite: { titre: string; description: string; typeLabel: string; partenaire?: string | null };
  }[];
  opportunitesDisponibles?: { id: string; titre: string; typeLabel: string; partenaire?: { nom: string } | null }[];
};

const FIELD_LABEL = 'text-xs font-bold text-on-surface-variant';
const FIELD_LABEL_BLOCK = 'block w-full min-w-0 text-sm';
const BTN_PRIMARY = 'academic-panel-btn academic-panel-btn--primary';
const BTN_SECONDARY = 'academic-panel-btn academic-panel-btn--secondary';

const ORIENTATION_STATUS: Record<string, { label: string; className: string }> = {
  PROPOSEE: { label: 'Proposition reçue', className: 'bg-amber-100 text-amber-900 ring-amber-200' },
  ACCEPTEE: { label: 'Acceptée', className: 'bg-emerald-100 text-emerald-900 ring-emerald-200' },
  REFUSEE: { label: 'Refusée', className: 'bg-slate-100 text-slate-700 ring-slate-200' },
  EN_COURS: { label: 'En cours', className: 'bg-sky-100 text-sky-900 ring-sky-200' },
  TERMINEE: { label: 'Terminée', className: 'bg-primary/10 text-primary ring-primary/20' },
};

export default function ApprenantExcellencePanel() {
  const [periode, setPeriode] = useState(() => `${new Date().getFullYear()}-S1`);
  const [periodeOptions, setPeriodeOptions] = useState<string[]>(buildDefaultPeriodeOptions);
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; variant: 'success' | 'error' } | null>(null);
  const [respondingId, setRespondingId] = useState<string | null>(null);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    setError(null);
    if (!options?.silent) setLoading(true);
    try {
      const row = (await fetchApprenantExcellence(periode)) as Dashboard;
      if (row.periodes?.length) {
        setPeriodeOptions(row.periodes);
        if (!row.periodes.includes(periode)) {
          setPeriode(row.periode ?? row.periodes[0]);
        }
      }
      setData(row);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Impossible de charger votre profil excellence.';
      setError(message);
      setToast({ message, variant: 'error' });
    } finally {
      if (!options?.silent) setLoading(false);
    }
  }, [periode]);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(() => void load({ silent: Boolean(data) }));

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToast({ message, variant });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const runAction = useCallback(
    async (
      action: () => Promise<unknown>,
      successMessage: string | ((result: unknown) => string),
      errorFallback = 'Action impossible.',
    ) => {
      try {
        const result = await action();
        const message =
          typeof successMessage === 'function' ? successMessage(result) : successMessage;
        showToast(message, 'success');
        await load({ silent: true });
      } catch (e) {
        showToast(e instanceof Error ? e.message : errorFallback, 'error');
      }
    },
    [load, showToast],
  );

  const score = data?.score;

  return (
    <>
      <ApprenantPageShell
      title="Excellence & employabilité"
      description="Votre classement, critères d'analyse et opportunités stages, emploi, mentorat et bourses."
      sideCard={{
        label: data?.estShortlist ? 'Shortlist excellence' : 'Score global',
        value: loading
          ? '…'
          : data?.estShortlist
            ? `#${score?.rang ?? '—'}`
            : score
              ? String(score.scoreGlobal)
              : '—',
        helper: data?.estShortlist
          ? 'Éligible aux avantages partenaires'
          : 'Améliorez vos critères pour rejoindre la shortlist',
        sideLinks: [
          { label: 'Tableau public', to: '/excellence', icon: 'emoji_events' },
          { label: 'Mes cours', to: '/app/mes-cours', icon: 'school' },
        ],
      }}
      isLoading={loading && !data}
      isError={Boolean(error) && !data}
      errorMessage={error ?? undefined}
      onRetry={() => void load({ silent: false })}
      loadingMessage="Chargement de votre profil excellence…"
    >
      {data ? (
        <div className="space-y-6">
          <div className="academic-panel-toolbar academic-panel-toolbar--spread !rounded-2xl !border !border-outline-variant/25 !shadow-sm">
            <label className={FIELD_LABEL_BLOCK}>
              <span className={FIELD_LABEL}>Période</span>
              <AcademiaSelect
                className="mt-1.5 w-full"
                value={periode}
                onChange={(e) => setPeriode(e.target.value)}
                aria-label="Période académique"
              >
                {periodeOptions.map((p) => (
                  <option key={p} value={p}>
                    {formatPeriodeLabel(p)}
                  </option>
                ))}
              </AcademiaSelect>
            </label>
          </div>

          {data.estShortlist ? (
            <div className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white px-4 py-4 text-sm text-amber-900 shadow-sm">
              <p className="flex items-center gap-2 font-bold">
                <LucideSymbolIcon name="emoji_events" tone="inherit" size={18} />
                Félicitations — vous êtes sur la shortlist excellence
              </p>
              <p className="mt-1">
                Stages, emploi, mentorat, bourses et formations avancées proposées par nos partenaires.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-outline-variant/25 bg-white px-4 py-3 text-sm shadow-sm">
              <p className="font-bold text-slate-900">Objectif shortlist</p>
              <p className="mt-1 text-on-surface-variant">
                Le classement est recalculé à chaque délibération. Améliorez notes, présences, discipline et
                projets.
              </p>
            </div>
          )}

          <AcademicPanelCard
            icon={<LucideSymbolIcon name="emoji_events" tone="primary" size={22} />}
            title="Mes critères d'excellence"
            subtitle={score ? `Score global ${score.scoreGlobal}` : 'En attente de délibération'}
            variant={data.estShortlist ? 'success' : 'default'}
          >
            {score ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {(data.criteres ?? []).map((c) => {
                  const value =
                    c.id === 'academique'
                      ? score.scoreAcademique
                      : c.id === 'presence'
                        ? score.scorePresence
                        : c.id === 'discipline'
                          ? score.scoreDiscipline
                          : c.id === 'implication'
                            ? score.scoreImplicationPedagogique
                            : c.id === 'pratique'
                              ? score.scorePratique
                              : c.id === 'projets'
                                ? score.scoreProjets
                                : 0;
                  return (
                    <article
                      key={c.id}
                      className="rounded-xl border border-outline-variant/20 bg-gradient-to-b from-white to-surface-container-low p-4"
                    >
                      <p className="text-xs font-bold uppercase text-on-surface-variant">{c.label}</p>
                      <p className="mt-1 text-2xl font-black text-primary">{value}</p>
                      <p className="text-xs text-on-surface-variant">Poids {(c.poids * 100).toFixed(0)} %</p>
                    </article>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-on-surface-variant">
                Aucun score pour cette période. Les résultats apparaissent après une délibération académique.
              </p>
            )}
          </AcademicPanelCard>

          <AcademicPanelCard
            icon={<ClipboardList className="h-5 w-5" aria-hidden />}
            title="Mes orientations employabilité"
            subtitle="Propositions reçues par notification — acceptez ou refusez ici"
            badge={
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-bold text-primary">
                {(data.orientations ?? []).length}
              </span>
            }
            flush
          >
            <AcademicPanelTableWrap
              empty={
                (data.orientations ?? []).length === 0 ? (
                  <div className="px-6 py-10 text-center">
                    <LucideSymbolIcon name="assignment" tone="muted" size={32} className="mx-auto opacity-60" />
                    <p className="mt-3 text-sm font-semibold text-slate-800">Aucune proposition</p>
                    <p className="mt-1 text-xs text-on-surface-variant">
                      Les opportunités vous seront notifiées par l&apos;administration.
                    </p>
                  </div>
                ) : undefined
              }
            >
              <ul className="divide-y divide-outline-variant/15">
                {(data.orientations ?? []).map((o) => {
                  const status = ORIENTATION_STATUS[o.statut] ?? {
                    label: o.statut,
                    className: 'bg-slate-100 text-slate-700 ring-slate-200',
                  };
                  return (
                    <AcademicPanelListItem key={o.id}>
                      <div className="space-y-3 px-4 py-4 text-sm">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="font-bold text-slate-900">{o.opportunite.titre}</p>
                            <p className="text-xs text-on-surface-variant">
                              {o.opportunite.typeLabel}
                              {o.opportunite.partenaire ? ` · ${o.opportunite.partenaire}` : ''}
                            </p>
                          </div>
                          <span
                            className={`rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ring-inset ${status.className}`}
                          >
                            {status.label}
                          </span>
                        </div>
                        <p className="text-on-surface-variant">{o.opportunite.description}</p>
                        {o.statut === 'PROPOSEE' ? (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={respondingId === o.id}
                              className={`${BTN_PRIMARY} !px-3 !py-1.5 text-xs disabled:opacity-50`}
                              onClick={async () => {
                                setRespondingId(o.id);
                                try {
                                  await runAction(
                                    () => respondExcellenceOrientation(o.id, true),
                                    (res) =>
                                      (res as { message?: string })?.message ??
                                      'Proposition acceptée avec succès.',
                                    'Impossible d\'accepter cette proposition.',
                                  );
                                } finally {
                                  setRespondingId(null);
                                }
                              }}
                            >
                              Accepter
                            </button>
                            <button
                              type="button"
                              disabled={respondingId === o.id}
                              className={`${BTN_SECONDARY} !px-3 !py-1.5 text-xs disabled:opacity-50`}
                              onClick={async () => {
                                setRespondingId(o.id);
                                try {
                                  await runAction(
                                    () => respondExcellenceOrientation(o.id, false),
                                    (res) =>
                                      (res as { message?: string })?.message ??
                                      'Proposition refusée.',
                                    'Impossible de refuser cette proposition.',
                                  );
                                } finally {
                                  setRespondingId(null);
                                }
                              }}
                            >
                              Refuser
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </AcademicPanelListItem>
                  );
                })}
              </ul>
            </AcademicPanelTableWrap>
          </AcademicPanelCard>

          {(data.opportunitesDisponibles ?? []).length > 0 ? (
            <AcademicPanelCard
              icon={<Briefcase className="h-5 w-5" aria-hidden />}
              title="Opportunités du réseau partenaires"
              subtitle="Programmes actifs pour votre profil"
            >
              <div className="grid gap-3 sm:grid-cols-2">
                {data.opportunitesDisponibles!.map((o) => (
                  <article
                    key={o.id}
                    className="rounded-xl border border-outline-variant/20 bg-white px-4 py-3 shadow-sm"
                  >
                    <p className="font-semibold text-slate-900">{o.titre}</p>
                    <p className="mt-1 text-xs text-on-surface-variant">
                      {o.typeLabel}
                      {o.partenaire?.nom ? ` · ${o.partenaire.nom}` : ''}
                    </p>
                  </article>
                ))}
              </div>
            </AcademicPanelCard>
          ) : null}

          <Link
            to="/excellence"
            target="_blank"
            className="inline-flex items-center gap-2 text-sm font-bold text-primary"
          >
            <LucideSymbolIcon name="emoji_events" tone="inherit" size={18} />
            Voir le tableau public des excellences
          </Link>
        </div>
      ) : null}
      </ApprenantPageShell>

      <AdminToast
        message={toast?.message ?? null}
        variant={toast?.variant ?? 'success'}
        onClose={dismissToast}
      />
    </>
  );
}
